/**
 * Hook for fetching app configuration from Supabase
 * 
 * Fetches store URLs and other configurable values from app_config table
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { StoreUrls } from '../bridges/types'

interface AppConfig {
  storeUrls: StoreUrls
  promptEnabled: boolean
  webFrameBaseUrl: string | null
}

async function fetchAppConfig(): Promise<AppConfig> {
  const { data, error } = await supabase
    .from('app_config')
    .select('key, value')
    .in('key', [
      'android_store_url',
      'ios_store_url',
      'store_prompt_enabled',
      'web_frame_base_url'
    ])
  
  if (error) {
    console.warn('Failed to fetch app config:', error)
    // Return defaults on error
    return {
      storeUrls: { android: null, ios: null },
      promptEnabled: true,
      webFrameBaseUrl: null
    }
  }
  
  // Convert to key-value map (value is jsonb so it's already parsed)
  const configMap: Record<string, any> = {}
  for (const row of data || []) {
    configMap[row.key] = row.value
  }
  
  // Helper to extract string value from jsonb (could be string or null)
  const getString = (value: any): string | null => {
    if (value === null || value === undefined) return null
    if (typeof value === 'string') return value || null
    return null
  }
  
  return {
    storeUrls: {
      android: getString(configMap['android_store_url']),
      ios: getString(configMap['ios_store_url'])
    },
    promptEnabled: configMap['store_prompt_enabled'] !== false,
    webFrameBaseUrl: getString(configMap['web_frame_base_url'])
  }
}

export function useAppConfig() {
  return useQuery({
    queryKey: ['app-config'],
    queryFn: fetchAppConfig,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,   // 30 minutes (formerly cacheTime)
  })
}

/**
 * Default store URLs - can be overridden from app_config
 */
export const DEFAULT_STORE_URLS: StoreUrls = {
  android: null, // Will be set from app_config
  ios: null      // Will be set from app_config
}

